import { Play } from '@phosphor-icons/react';
import {
  activeAt,
  type CaptionCanvas,
  cueMarkup,
  drawCaption,
  type ReelTimeline,
  retimeWords,
  type SpokenWord,
  subtitleCues,
} from '@reelplicate/flows';
import { cn } from '@reelplicate/ui';
import { useEffect, useMemo, useRef } from 'react';

import { useCaptionFonts } from '@/features/caption-styles/caption-fonts';

import type { Playback } from './use-playback';

/**
 * The reel as edited, at the playhead: the source video playing the cuts,
 * and its text and subtitles drawn on a canvas the size of the video with
 * the same drawing code and fonts the render uses - what you see is what
 * gets rendered.
 */
export function PreviewStage({
  sourceUrl,
  width,
  height,
  timeline,
  words,
  playback,
  className,
}: {
  sourceUrl: string;
  width: number;
  height: number;
  timeline: ReelTimeline;
  words: readonly SpokenWord[];
  playback: Playback;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fontsReady = useCaptionFonts();
  const { time, playing, toggle, videoRef } = playback;

  const cues = useMemo(() => {
    const subtitles = timeline.subtitles;
    if (!subtitles?.enabled || !words.length) return [];
    return subtitleCues(retimeWords(words, timeline.clips), subtitles.mode);
  }, [timeline.subtitles, timeline.clips, words]);

  // What shows now; the canvas redraws only when that changes.
  const texts = activeAt(timeline.texts, time);
  const cue = activeAt(cues, time)[0];
  const showing = JSON.stringify([texts, cue && cueMarkup(cue), timeline.subtitles]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    const canvas = ctx as unknown as CaptionCanvas;
    for (const text of texts) drawCaption(canvas, { width, height, ...text });
    if (cue && timeline.subtitles)
      drawCaption(canvas, {
        width,
        height,
        text: cueMarkup(cue),
        position: timeline.subtitles.position,
        style: timeline.subtitles.style,
      });
    // `showing` stands for texts, cue and subtitles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showing, fontsReady, width, height]);

  return (
    <div
      className={cn('relative overflow-hidden rounded-md bg-black shadow-sm', className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <video
        ref={videoRef}
        src={sourceUrl}
        playsInline
        preload="auto"
        aria-label="Reel preview"
        className="absolute inset-0 size-full object-contain"
        onLoadedMetadata={() => playback.seek(time)}
      />
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        aria-hidden
        className="pointer-events-none absolute inset-0 size-full"
      />
      <button
        type="button"
        aria-label={playing ? 'Pause' : 'Play'}
        onClick={toggle}
        className="focus-visible:shadow-focus-ring absolute inset-0 flex items-center justify-center outline-none"
      >
        {!playing && (
          <span className="motion-safe:animate-in motion-safe:fade-in-0 flex size-14 items-center justify-center rounded-full bg-black/55 text-white duration-150">
            <Play aria-hidden weight="fill" className="size-6" />
          </span>
        )}
      </button>
    </div>
  );
}
